import { type SkuId, storesWhichSupportRecommendationsCalculation } from '@bluebrick/discounts-service-module'
import { groupBy } from 'lodash-es'
import { type DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { isDiscountEvent, isCooldownEvent, isStoreOpportunityEvent, isOpportunityEvent, hasOverlap } from '@/common/utils'
import { useEventDetailsBaseStore } from '@/features/eventDetails/useEventDetailsBaseStore'
import { useEventsStore } from '@/features/events/useEventsStore'
import { EventType, type EventData } from '@/types'

export function useEventDetailsStoreData() {
	const { storeId, sourceEvent, startDate, endDate } = storeToRefs(useEventDetailsBaseStore())
	const { storeEvents, selectedSKUIdsForStore, storeSKUs } = storeToRefs(useEventsStore())

	const selectedSkuIds = computed(() => selectedSKUIdsForStore.value(storeId.value) ?? [])
	const canCalculateEventParameters = computed(() => storeId.value && storesWhichSupportRecommendationsCalculation.includes(storeId.value))
	const allSkusInStore = computed(() => storeSKUs.value(storeId.value) ?? [])
	const storeData = computed(() => (storeId.value ? storeEvents.value.get(storeId.value) : undefined))

	// Forward and backward cooldowns associated with current source event
	const sourceEventCooldowns = computed(() => {
		if (
			!storeId.value ||
			!sourceEvent.value ||
			sourceEvent.value.event_type !== EventType.Discount ||
			!sourceEvent.value.start_date.isValid ||
			!sourceEvent.value.end_date.isValid
		) {
			return []
		}

		const sourceSKUEvents = storeData.value?.skus?.find((sku) => sku.unique_sku_id === sourceEvent.value?.unique_sku_id)?.events

		if (!sourceSKUEvents) {
			return []
		}

		return sourceSKUEvents.filter(
			(cooldownEvent) =>
				cooldownEvent.event_type === EventType.Cooldown &&
				// backward cooldown
				((cooldownEvent.start_date < <DateTime>sourceEvent.value?.start_date &&
					cooldownEvent.end_date.toISO() === sourceEvent.value?.start_date?.toISO()) ||
					// forward cooldown
					(cooldownEvent.end_date > <DateTime>sourceEvent.value?.end_date &&
						cooldownEvent.start_date.toISO() === sourceEvent.value?.end_date?.toISO()))
		)
	})

	/**
	 * Retuns lists of events and cooldowns to be used by data fetching queries.
	 * These events *do not include* source event (if any event is selected) or source event's cooldowns.
	 */
	function getEventsPayloads() {
		const eventsForSelectedSkus: EventData[] = []
		const cooldownsForSelectedSkus: EventData[] = []

		const opportunities: EventData[] = []
		const discountEvents: EventData[] = []
		const skuIdsWithLocalRecommendations: SkuId[] = []

		storeData.value?.skus
			?.filter((sku) => selectedSkuIds.value.includes(sku.unique_sku_id))
			?.forEach((sku) => {
				for (const event of sku.events) {
					if (
						!isEventInsideConsideredRange(event, startDate.value) ||
						(sourceEvent.value && isSourceEventOrItsCooldown(event, sourceEvent.value, sourceEventCooldowns.value))
					) {
						continue
					}

					if (isDiscountEvent(event)) {
						eventsForSelectedSkus.push(event)
						discountEvents.push(event)
					}

					if (isStoreOpportunityEvent(event)) {
						discountEvents.push(event)
					}

					if (isOpportunityEvent(event)) {
						opportunities.push(event)
					}

					if (isCooldownEvent(event)) {
						cooldownsForSelectedSkus.push(event)
					}
				}
			})

		// In case of newly created event first check if there is an opportunity that current dates overlap with
		// If there is then use that opportunity's dates as recommendation
		if (!sourceEvent.value?.id) {
			const opportunitiesBySKU = groupBy(opportunities, 'unique_sku_id')
			const eventDatesDraft = {
				start_date: startDate.value,
				end_date: endDate.value
			}

			Object.entries(opportunitiesBySKU).forEach(([skuId, opportunitiesForSKU]) => {
				const matchingOpportunity = opportunitiesForSKU.find((opportunity) => hasOverlap(opportunity, eventDatesDraft))

				if (matchingOpportunity) {
					skuIdsWithLocalRecommendations.push(skuId)
				}
			})
		}

		return {
			eventsForSelectedSkus,
			cooldownsForSelectedSkus,
			// If we have local recommendation for some SKU(s) then remove related events from list
			// to reduce the processing in API when asking for remote recommendations
			discountsForDatesRecommendations: discountEvents.filter(
				({ unique_sku_id }) => unique_sku_id && !skuIdsWithLocalRecommendations.includes(unique_sku_id)
			)
		}
	}

	// Checks whether event is inside considered range, which is -3 months and +6 months
	// other events are not important for context and max length can be calculated using only this 9 months, instead of sending 3 years of events
	function isEventInsideConsideredRange(event: EventData, startDate: DateTime): boolean {
		const startRange = startDate.minus({ month: 3 })
		const endRange = startDate.plus({ month: 6 })

		if (!startRange.isValid || !endRange.isValid) {
			return true
		}

		return event.end_date > startRange && event.start_date < endRange
	}

	/**
	 * Returns true if given event is the same as current source event or if given event is one
	 * of the cooldowns of current source event.
	 */
	function isSourceEventOrItsCooldown(event: EventData, sourceEvent: EventData, sourceEventsCooldowns: EventData[]): boolean {
		if (!sourceEvent) {
			return false
		}

		if (sourceEvent.hash === event.hash) {
			// Is the same as source event
			return true
		}

		// True if event is one of the cooldowns of current source event
		return sourceEventsCooldowns.some((cooldown) => cooldown.hash === event.hash)
	}

	return {
		canCalculateEventParameters,
		storeSKUs: allSkusInStore,
		selectedSkuIds,
		storeData,
		sourceEventCooldowns,
		getEventsPayloads
	}
}
